import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { FiDownload } from 'react-icons/fi';
import { getInstances, getAggregateHistory } from '../../services/monitorService';
import { fmtMoney, fmtProfit, profitClass, fmtTime } from '../../utils/format';

const Statistics = () => {
  const { data: instancesData, isLoading } = useQuery('instances', getInstances, { refetchInterval: 60000 });
  // accounts: 按 MT5 账户归组(同账户多图表合并, 资金指标不重复计算)
  const accounts = instancesData?.accounts || [];
  const summary = instancesData?.summary || {};

  const [range, setRange] = React.useState('24h');

  // 全实例聚合曲线(分桶求和, 单实例明细去 实时监控→实例总览 看)
  const { data: aggregateData } = useQuery(
    ['aggregateHistory', range],
    () => getAggregateHistory(range),
    { refetchInterval: 60000 }
  );

  const points = aggregateData?.points || [];
  const chartData = points.map(p => ({
    t: fmtTime(p.t).slice(5),
    equity: Number(p.equity) || 0,
    dayProfit: Number(p.dayProfit) || 0
  }));

  // 当日盈亏排行(按账户, 同账户多图表只算一次)
  const ranked = [...accounts].sort((a, b) => (Number(b.dayProfit) || 0) - (Number(a.dayProfit) || 0));

  // 导出 CSV(全实例聚合历史)
  const exportCsv = () => {
    if (points.length === 0) {
      alert('当前没有可导出的数据');
      return;
    }
    const header = '时间,总余额,总净值,总浮动盈亏,总当日盈亏,实例数\n';
    const lines = points.map(p =>
      [fmtTime(p.t), p.balance ?? '', p.equity ?? '', p.floatingProfit ?? '', p.dayProfit ?? '', p.instanceCount ?? ''].join(',')
    );
    const blob = new Blob(['\ufeff' + header + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stats_all_instances_${range}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-40" />
        <div className="skeleton h-72" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 页头 */}
      <div>
        <h1 className="text-2xl font-semibold text-[#1f2328]">数据统计</h1>
        <p className="text-[#656d76] text-sm mt-1">账户资金历史曲线与盈亏报表</p>
      </div>

      {/* 汇总卡片 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
          <p className="text-[#656d76] text-xs mb-1">在线账户</p>
          <p className="text-xl font-semibold text-[#1f2328]">{summary.online ?? 0} / {summary.total ?? 0}</p>
        </div>
        <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
          <p className="text-[#656d76] text-xs mb-1">总净值</p>
          <p className="text-xl font-semibold text-[#1f2328]">{fmtMoney(summary.totalEquity)}</p>
        </div>
        <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
          <p className="text-[#656d76] text-xs mb-1">当日盈亏</p>
          <p className={`text-xl font-semibold ${profitClass(summary.dayProfit)}`}>{fmtProfit(summary.dayProfit)}</p>
        </div>
        <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
          <p className="text-[#656d76] text-xs mb-1">盈利账户 / 亏损账户</p>
          <p className="text-xl font-semibold text-[#1f2328]">
            <span className="text-[#cf222e]">{ranked.filter(i => (Number(i.dayProfit) || 0) > 0).length}</span>
            <span className="text-[#656d76] font-normal"> / </span>
            <span className="text-[#1a7f37]">{ranked.filter(i => (Number(i.dayProfit) || 0) < 0).length}</span>
          </p>
        </div>
      </div>

      {/* 全实例聚合曲线 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-[#1f2328] font-medium">全部实例资金曲线</h2>
          <span className="text-[#8b949e] text-xs">所有账户分桶汇总（同账户多图表只算一次） · 单账户曲线请到 实时监控→实例总览→明细</span>
          <div className="flex gap-1">
            {['1h', '6h', '24h', '7d'].map(r => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`text-xs px-2.5 py-1 rounded border ${
                  range === r ? 'bg-[#ddf4ff] text-[#0969da] border-[#b6e3ff]' : 'bg-white text-[#57606a] border-[#d0d7de] hover:bg-[#f6f8fa]'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <button
            onClick={exportCsv}
            className="ml-auto flex items-center gap-1 text-xs text-[#0969da] border border-[#d0d7de] rounded-md px-2.5 py-1.5 bg-white hover:bg-[#f6f8fa]"
          >
            <FiDownload /> 导出 CSV
          </button>
        </div>

        {points.length === 0 ? (
          <div className="py-16 text-center text-[#656d76] text-sm">暂无聚合曲线数据（等待 EA 上报）</div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eaeef2" />
              <XAxis dataKey="t" tick={{ fontSize: 11, fill: '#656d76' }} minTickGap={40} />
              <YAxis tick={{ fontSize: 11, fill: '#656d76' }} width={70} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '0.5px solid #d0d7de' }}
                formatter={(value, name) => [fmtMoney(value), name === 'equity' ? '净值' : '当日盈亏']}
              />
              <Legend formatter={(v) => v === 'equity' ? '净值' : '当日盈亏'} />
              <Line type="monotone" dataKey="equity" stroke="#0969da" strokeWidth={2} dot={false} name="equity" />
              <Line type="monotone" dataKey="dayProfit" stroke="#cf222e" strokeWidth={1.5} dot={false} name="dayProfit" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* 盈亏排行 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm">
        <div className="px-5 py-3 border-b border-[#d0d7de]">
          <h2 className="text-[#1f2328] font-medium">当日盈亏排行（按账户）</h2>
        </div>
        {ranked.length === 0 ? (
          <div className="px-5 py-10 text-center text-[#656d76] text-sm">暂无账户数据</div>
        ) : (
          <div className="divide-y divide-[#eaeef2]">
            {ranked.map((acct, idx) => (
              <div key={acct.accountKey} className="px-5 py-3 flex items-center gap-4">
                <span className={`w-6 text-center text-sm font-medium ${idx < 3 ? 'text-[#9a6700]' : 'text-[#8b949e]'}`}>
                  {idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#1f2328] truncate">
                    {acct.accountLogin || '未知账户'}
                    {acct.accountServer && <span className="text-[#656d76] font-normal"> · {acct.accountServer}</span>}
                    {!acct.isOnline && <span className="ml-2 text-[10px] px-1 rounded bg-[#f6f8fa] text-[#57606a]">离线</span>}
                    {acct.instanceCount > 1 && <span className="ml-2 text-[10px] px-1 rounded bg-[#ddf4ff] text-[#0969da]">{acct.instanceCount} 图表</span>}
                  </div>
                  <div className="text-[#8b949e] text-xs font-mono truncate">{acct.licenseKey}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-sm font-medium ${profitClass(acct.dayProfit)}`}>{fmtProfit(acct.dayProfit)}</div>
                  <div className="text-[#8b949e] text-xs">净值 {fmtMoney(acct.equity)}</div>
                </div>
                <Link
                  to={`/monitor/instances/${encodeURIComponent(acct.instances[0].instanceId)}`}
                  className="shrink-0 text-[#0969da] text-xs hover:underline hidden sm:block"
                >
                  明细
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Statistics;
